"use client";

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { searchArticles, getPopularTags, getSearchSuggestions, SearchFilters, SearchResult } from '@/lib/articleSearch';
import { ARTICLE_CATEGORIES, getAllCategories } from '@/lib/articleCategories';
import styles from './ArticleSearch.module.css';
import Icon, { isIconName } from './Icon';

interface ArticleSearchProps {
  onResults: (results: SearchResult[]) => void;
  onLoading: (loading: boolean) => void;
  className?: string;
}

export default function ArticleSearch({ onResults, onLoading, className }: ArticleSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'title' | 'relevance'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [popularTags, setPopularTags] = useState<{ tag: string; count: number }[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const categories = getAllCategories();

  // Load popular tags on mount
  useEffect(() => {
    getPopularTags(10).then(setPopularTags);
  }, []);

  // Generate search suggestions as user types
  useEffect(() => {
    if (searchQuery.length > 1) {
      const timeoutId = setTimeout(() => {
        getSearchSuggestions(searchQuery, 5).then(setSuggestions);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setSuggestions([]);
    }
  }, [searchQuery]);

  // Perform search
  const handleSearch = useCallback(async () => {
    setIsSearching(true);
    onLoading(true);

    const filters: SearchFilters = {
      query: searchQuery || undefined,
      category: selectedCategory || undefined,
      sortBy,
      sortOrder,
    };

    try {
      const results = await searchArticles(filters);
      onResults(results);
    } catch (error) {
      console.error('Search error:', error);
      onResults([]);
    } finally {
      setIsSearching(false);
      onLoading(false);
    }
  }, [searchQuery, selectedCategory, sortBy, sortOrder, onResults, onLoading]);

  // Clear all filters
  const handleClear = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSortBy('date');
    setSortOrder('desc');
    setSuggestions([]);
  };

  // Auto-search when filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [handleSearch]);

  const handleSuggestionClick = (suggestion: string) => {
    setSearchQuery(suggestion);
    setSuggestions([]);
  };

  const handleTagClick = (tag: string) => {
    setSearchQuery(tag);
  };

  const handleCategorySelect = (categorySlug: string) => {
    setSelectedCategory(categorySlug === selectedCategory ? '' : categorySlug);
  };

  return (
    <div className={`${styles.searchContainer} ${className || ''}`}>
      <div className={styles.searchForm}>
        {/* Main search input */}
        <div className={styles.searchInputGroup}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search articles by title, content, or tags..."
            className={styles.searchInput}
          />
          <svg className={styles.searchIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        {/* Search suggestions */}
        {suggestions.length > 0 && (
          <div className={styles.searchSuggestions}>
            <div className={styles.suggestionsTitle}>Suggestions:</div>
            <div className={styles.suggestionsList}>
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={styles.suggestionItem}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Advanced filters toggle */}
        <div className={styles.searchActions}>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={styles.clearButton}
          >
            {showAdvanced ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button onClick={handleClear} className={styles.clearButton}>
            Clear All
          </button>
        </div>

        {/* Advanced filters */}
        {showAdvanced && (
          <div className={styles.filtersRow}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Sort By</label>
              <select
                title='Sort By'
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'date' | 'title' | 'relevance')}
                className={styles.filterSelect}
              >
                <option value="date">Date</option>
                <option value="title">Title</option>
                {searchQuery && <option value="relevance">Relevance</option>}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Order</label>
              <select
                title='Sort Order'
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                className={styles.filterSelect}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Categories */}
      <div className={styles.categoriesGrid}>
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => handleCategorySelect(category.slug)}
            className={`${styles.categoryCard} ${
              selectedCategory === category.slug ? styles.active : ''
            }`}
          >
            <div className={styles.categoryIcon}>
              {isIconName(category.icon) && <Icon name={category.icon} size={20} />}
            </div>
            <div className={styles.categoryInfo}>
              <h3 className={styles.categoryName}>{category.name}</h3>
              <p className={styles.categoryDescription}>{category.description}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Popular tags */}
      {popularTags.length > 0 && !searchQuery && (
        <div className={styles.popularTags}>
          <h3 className={styles.tagsTitle}>Popular Topics</h3>
          <div className={styles.tagsList}>
            {popularTags.map((tag, index) => (
              <button
                key={index}
                onClick={() => handleTagClick(tag.tag)}
                className={styles.tagItem}
              >
                {tag.tag}
                <span className={styles.tagCount}>({tag.count})</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
